/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { AppSettings, AzureAccount } from 'src/core/stores/app/types';
import { CultureStore } from 'src/core/stores/CultureStore';
import { AppAzureCsAccountSwagger, AppCultureSwagger, AppSettingsSwagger } from 'src/luis/core/services/apps/appSwaggers';
import { createAppPermissionsStore } from 'src/luis/core/stores/appPermissionsStore';
import { createAppVersionStore } from 'src/luis/core/stores/appVersionStore';
import { ConversationPublishSlotStore } from 'src/luis/core/stores/ConversationPublishSlotStore';
import { LuisAppStore } from 'src/luis/core/stores/LuisAppStore';
import { TrainingStore } from 'src/luis/core/stores/TrainingStore';
import { AppPermissionsSwagger } from 'src/luis/projects/apps/types/swagger/AppPermissionsSwagger';
import { AppSwagger, EndpointItemSwagger } from 'src/luis/projects/apps/types/swagger/AppSwagger';
import { AppVersionSwagger } from 'src/luis/projects/apps/types/swagger/AppVersionSwagger';
import { AppPermissionsStore } from 'src/luis/types/stores/apps/AppPermissionsStore';
import { AppVersionStore } from 'src/luis/types/stores/apps/AppVersionStore';
import { TrainingModes, TrainingState } from 'src/luis/types/trainingTypes';

export const luisAppSwaggerToLuisAppStore = (appItem: Partial<AppSwagger>): LuisAppStore => {
    const publishSlots: LuisAppStore['publishSlots'] = { staging: null, production: null };

    if (appItem.endpoints.STAGING) {
        publishSlots.staging = endPointItemToPublishSlotStore(appItem.endpoints.STAGING);
    }

    if (appItem.endpoints.PRODUCTION) {
        publishSlots.production = endPointItemToPublishSlotStore(appItem.endpoints.PRODUCTION);
    }

    return new LuisAppStore(appItem.id, {
        publishSlots,
        name: appItem.name,
        culture: appItem.culture,
        description: appItem.description,
        activeVersion: appItem.activeVersion,
        endpointHitsCount: appItem.endpointHitsCount,
        createdDate: new Date(appItem.createdDateTime),
        modifiedDate: appItem.lastModifiedDateTime ? new Date(appItem.lastModifiedDateTime) : null,
        lastPublishedDate: publishSlots.staging?.lastPublishedDate ?? publishSlots.production?.lastPublishedDate
    });
};

export const appCultureSwaggerToAppCultureStore = (appCultureSwagger: AppCultureSwagger) => new CultureStore(appCultureSwagger);

export const appSettingsSwaggerToAppSettingsStore = (appSettings: AppSettingsSwagger): AppSettings => ({
    id: appSettings.id,
    isPublic: appSettings.public
});

export const appPermissionsSwaggerToAppPermissionsStore = (appPermissionsSwagger: AppPermissionsSwagger): AppPermissionsStore =>
    createAppPermissionsStore({ owner: appPermissionsSwagger.owner, collaborators: appPermissionsSwagger.emails });

export const appVersionSwaggerToAppVersionStore = (appVersionItem: Partial<AppVersionSwagger>): AppVersionStore => {
    let overallStatus: TrainingState = 'none';
    let lastTrainingMode: TrainingModes = 'Standard';
    const isDirty = appVersionItem.trainingStatus !== 'Trained';
    const trainedDate = appVersionItem.lastTrainedDateTime && new Date(appVersionItem.lastTrainedDateTime);

    if (appVersionItem.trainingMode === 'Neural') {
        lastTrainingMode = 'Neural';
    }

    if (isDirty && appVersionItem.trainingStatus === 'InProgress') {
        overallStatus = 'inProgress';
    } else if (!isDirty) {
        overallStatus = 'upToDate';
    }

    return createAppVersionStore(appVersionItem.version, {
        isDirty,
        trainedDate,
        intentsCount: appVersionItem.intentsCount,
        entitiesCount: appVersionItem.entitiesCount,
        endpointHitsCount: appVersionItem.endpointHitsCount,
        createdDate: new Date(appVersionItem.createdDateTime),
        modifiedDate: new Date(appVersionItem.lastModifiedDateTime),
        publishedDate: appVersionItem.lastPublishedDateTime && new Date(appVersionItem.lastPublishedDateTime),
        trainingStore: new TrainingStore({ lastTrainingMode, isTrained: Boolean(trainedDate), currentTrainingStatus: { overallStatus } })
    });
};

export const endPointItemToPublishSlotStore = (endpoint: Partial<EndpointItemSwagger>) =>
    new ConversationPublishSlotStore({
        versionId: endpoint.versionId,
        isStaging: endpoint.isStaging,
        lastPublishedDate: endpoint.publishedDateTime && new Date(endpoint.publishedDateTime)
    });

export const appAzureCsAccountSwaggerToAppAzureCsAccountStore = (swagger: AppAzureCsAccountSwagger): AzureAccount => ({
    id: swagger.EndpointId,
    path: swagger.KeyPath,
    region: swagger.Region,
    accountName: '',
    kind: '',
    resourceGroup: ''
});
